import { ChangeEvent, ForwardedRef, forwardRef } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { ButtonProps } from '@mui/material';
import BigNumber from 'bignumber.js';

import {
  ITextFieldProps,
  TextField,
} from 'modules/common/components/TextField';
import { globalTranslation, useTranslation } from 'modules/i18n';

interface INumberTextFieldProps<T extends FieldValues> extends ITextFieldProps {
  name: Path<T>;
  control: Control<T>;
  min: BigNumber;
  unit: string;
  integer?: boolean;
  maxButtonProps?: ButtonProps;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const regExp = /^$|^(\d{0,10})(\.\d{0,18})?$/;

function NumberTextFieldComponent<T extends FieldValues>(
  {
    name,
    control,
    balance,
    onChange,
    min,
    unit,
    integer,
    ...props
  }: INumberTextFieldProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { t, keys } = useTranslation(globalTranslation);

  const { field } = useController({
    name,
    control,
    rules: {
      required: t(keys.validation.required),
      max: { value: balance?.toNumber() ?? 0, message: t(keys.validation.max) },
      min: {
        value: min.toNumber(),
        message: t(keys.validation.min, {
          value: min.toFormat(),
          unit,
        }),
      },
    },
  });

  const handleChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value } = event.target;

    if (integer && value.includes('.')) {
      return;
    }

    if (regExp.exec(value)) {
      if (onChange) {
        onChange(event);
      }

      field.onChange(event);
    }
  };

  return (
    <TextField
      ref={ref}
      autoComplete="off"
      balance={balance}
      value={field.value}
      onChange={handleChange}
      {...props}
    />
  );
}

export const NumberTextField = forwardRef(NumberTextFieldComponent);
